import { formatAddress, formatDate, formatName } from "@tellescope/utils";
import { formatStoredTime } from "@tellescope/forms";
import { RichTextDisplay } from "@tellescope/ui";
import { ReactNode } from "react";
import { Answer } from "./types";
import { HIDDEN_ANSWER_TYPES } from "./constants";
import { i18n } from "@tellescope/i18n";

export const renderAnswer = (answer: Answer): ReactNode => {
  if (!answer.value)
    return <p className="text-xs text-muted-foreground italic">{i18n.t("(no response)", { ns: "forms" })}</p>;
  if (HIDDEN_ANSWER_TYPES.includes(answer.type)) return null;

  switch (answer.type) {
    case "string":
    case "description":
    case "stringLong":
    case "email":
    case "phone":
    case "Timezone":
    case "Emotii":
    case "Stripe":
    case "Dropdown":
    case "Appointment Booking":
      return answer.value;
    case "number":
      return answer.value.toLocaleString();
    case "files":
    case "file": {
      const files = Array.isArray(answer.value) ? answer.value : [answer.value];
      return files.map((file) => `${file.name} ${file.type ? `(${file.type})` : ""}`.trim()).join(", ");
    }
    case "Rich Text":
      return <RichTextDisplay content={answer.value} />;
    case "date":
    case "dateString":
      return formatDate(answer.value);
    case "rating":
      return `${answer.value}/10`;
    case "Time":
      return formatStoredTime(answer.value);
    case "Allergies":
      return answer.value.map((allergy) => allergy.display).join(", ");
    case "Chargebee":
      return answer.value.url;
    case "Address":
      return formatAddress(answer.value);
    case "Table Input":
      return answer.value.map((row) => row.map((cell) => `${cell.label}: ${cell.entry}`).join(", "));
    case "ranking":
      return answer.value.map((value, i) => `${i}. ${value}`).join("\n");
    case "signature":
      return `${answer.value.signed ? "Signed" : "Not Signed"}: ${answer.value.fullName}`;
    case "Medications":
      return answer.value.map((medication) => medication.displayTerm).join(", ");
    case "Database Select":
      return answer.value.map((item) => item.text).join(", ");
    case "multiple_choice":
      return answer.value.join(", ");
    case "Related Contacts":
      return answer.value.map((contact) => formatName(contact)).join(", ");
    case "Insurance": {
      const elements: string[] = [];
      if (answer.value.memberId) {
        elements.push(`Member ID: ${answer.value.memberId}`);
      }
      if (answer.value.groupNumber) {
        elements.push(`Group Number: ${answer.value.groupNumber}`);
      }
      if (answer.value.eligibility) {
        elements.push(`Eligibility: ${answer.value.eligibility}`);
      }
      return elements.join(", ");
    }
    case "Height":
      return `${answer.value.feet}' ${answer.value.inches}`;
    default:
      return null;
  }
};
